// Neu (Rückmeldung 2026-09-30: „in general make all jobs visible with progress“ — nach dem
// PDF-Import war nicht zu sehen, dass überhaupt etwas passiert). Der Aufträge-Knopf der
// Kopfleiste zeigt ALLE Arbeit eines Verfahrens an einer Stelle:
//   · Browser: Hochladen (Schlange des Eingangs), Import aus dem Eingangsordner, Exporte in
//     Erstellung (useIsMutating auf `exportSchluessel(V)`);
//   · Server (GET /v/{V}/auftraege, jede Zahl aus dem Zählwerk): die Aufbereitung nach dem Import
//     (raster → maske → boegen; nach dem Lesen „Kästchen messen“, E23), „Ortsverzeichnis laden“
//     (E21, eigener Abschnitt), der Lese-Lauf auf GEX44 (Einheit je Schritt, 05 §12),
//     GPU-fremd-belegt, Fehler der 24 h.
// Knopf: Ring dreht sich, solange etwas läuft; Plakette = offene Posten; roter Punkt = Fehler.
// Panel: Hausbaustein `Menue` (Esc, Tipp daneben, zweiter Tipp auf den Knopf, Wegwechsel
// schließen es); am Telefon volle Breite unter der Kopfleiste wie die Verfahren-Wahl.
// Unter der Kopfleiste läuft eine 3-px-Linie mit dem Gesamtfortschritt (AuftraegeLinie).
// Abfrage ["auftraege", V]: 3 s, solange etwas läuft, sonst 30 s, nie im verborgenen Tab; der
// Live-Strom macht sie bei jedem `auftraege`-Ereignis sofort ungültig (hooks/useLive.ts).
import { useCallback, useEffect, useId, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIsMutating, useQuery } from "@tanstack/react-query";
import { holeAuftraege } from "../../api/client";
import type { AuftraegeStand, AuftragsGruppe } from "../../api/typen";
import { Chip, Icon, Menue } from "../ui";
import { Fortschritt } from "../ladewerk";
import { useIch } from "../../hooks/useIch";
import { auftragsZahlen, exportSchluessel, offen, plakette, restzeit, TAKT_AKTIV_MS, TAKT_RUHE_MS, zuletztFertig, type AuftragsZahlen } from "../../lib/auftraege";
import { uhrzeit } from "../../lib/format";
import { hatRecht } from "../../lib/rechte";
import { auftragLabel, pausenSatz, TX } from "../../lib/texte";
import { useStore, type ClientAuftraege, type HochladePosten } from "../../store/useStore";
import "../../styles/af-auftraege.css";

/* ================================================================ Stand == */

export interface AuftraegeLage {
  daten: AuftraegeStand | undefined;
  ladeFehler: boolean;
  client: ClientAuftraege;
  exporte: number;
  zahlen: AuftragsZahlen;
  /** Gibt es Browser-Arbeit (auch fertige/fehlgeschlagene Uploads in der Schlange)? */
  clientDa: boolean;
}

/** Ein Stand für Knopf, Panel und Linie — die Abfrage teilen sich alle drei (gleicher Schlüssel).
 *  `takt`: nur EIN Beobachter darf den Abfragetakt führen (der Knopf) — jeder Beobachter mit
 *  eigenem refetchInterval stellt eine eigene Uhr, zwei Uhren hießen doppelt so viele Abrufe. */
export function useAuftraegeLage(V: string | null, takt = true): AuftraegeLage {
  const { data: ich } = useIch();
  const darf = !!V && hatRecht(ich, V, "lesen");
  const client = useStore((s) => s.auftraege);
  const exporte = useIsMutating({ mutationKey: exportSchluessel(V) });
  const clientAktiv = !!client.import || exporte > 0 || !!client.hochladen?.posten.some((p) => p.zustand === "wartet" || p.zustand === "laedt");
  const q = useQuery({
    queryKey: ["auftraege", V ?? ""],
    queryFn: () => holeAuftraege(V ?? ""),
    enabled: darf,
    staleTime: 1000,
    retry: 1,
    refetchInterval: takt ? (abfrage) => ((abfrage.state.data as AuftraegeStand | undefined)?.aktiv || clientAktiv ? TAKT_AKTIV_MS : TAKT_RUHE_MS) : false,
    refetchIntervalInBackground: false,
  });
  const daten = darf ? q.data : undefined;
  return {
    daten,
    ladeFehler: darf && q.isError,
    client,
    exporte,
    zahlen: auftragsZahlen(daten, client, exporte),
    clientDa: !!client.import || exporte > 0 || !!client.hochladen?.posten.length,
  };
}

/* ================================================================ Knopf == */

export function Auftraege({ V }: { V: string | null }) {
  const [offenM, setOffen] = useState(false);
  const schliessen = useCallback(() => setOffen(false), []);
  const menueId = useId();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const lage = useAuftraegeLage(V);
  const z = lage.zahlen;

  // Wegwechsel schließt (auch der Tipp auf einen Abschnittslink).
  useEffect(() => setOffen(false), [pathname]);

  if (!V && !lage.clientDa) return null;
  const label = TX.auftraege.knopf(z.offen, z.fehler > 0);
  return (
    <div className="menue-anker af-anker">
      <button
        type="button"
        className={`af-knopf ring-focus${z.aktiv ? " laeuft" : ""}${offenM ? " offen" : ""}`}
        aria-haspopup="menu"
        aria-expanded={offenM}
        aria-controls={offenM ? menueId : undefined}
        aria-label={label}
        title={label}
        data-testid="auftraege-knopf"
        // Wie die Verfahren-Wahl: ohne diese Zeile schlösse der mousedown („Tipp daneben“) das
        // Menü, und der folgende click öffnete es sofort wieder — der zweite Tipp muss schließen.
        onMouseDown={(e) => e.stopPropagation()}
        onClick={() => setOffen((o) => !o)}
      >
        {z.aktiv && <span className="af-ring" aria-hidden="true" />}
        <Icon name="list-checks" size={19} />
        {z.offen > 0 && (
          <span className="af-zahl" aria-hidden="true" data-testid="auftraege-zahl">
            {plakette(z.offen)}
          </span>
        )}
        {z.fehler > 0 && <span className={`af-fehlerpunkt${z.offen > 0 ? " unten" : ""}`} aria-hidden="true" data-testid="auftraege-fehlerpunkt" />}
      </button>
      <Menue
        open={offenM}
        onClose={schliessen}
        width={400}
        align="right"
        id={menueId}
        label={TX.auftraege.titel}
        style={{ maxHeight: "min(640px, calc(100dvh - var(--kopf-h) - var(--safe-top) - 24px))", overflowY: "auto", overscrollBehavior: "contain" }}
      >
        <AuftraegePanel
          V={V}
          lage={lage}
          weiter={(zu) => {
            setOffen(false);
            navigate(zu);
          }}
        />
      </Menue>
    </div>
  );
}

/* ================================================================ Panel == */

const offenPosten = (p: HochladePosten) => p.zustand === "wartet" || p.zustand === "laedt";
const mb = (bytes: number) => Math.round((bytes / 1_048_576) * 10) / 10;

/** E35: Schritte der Bogenvorlage aus dem leeren Bogen (CPU je Verfahren, GPU je Blatt, CPU je Seite). */
const FORMULAR_SCHRITTE: ReadonlySet<string> = new Set(["leerbogen", "formular_lesen", "vorlage"]);

export function AuftraegePanel({ V, lage, weiter }: { V: string | null; lage: Pick<AuftraegeLage, "daten" | "ladeFehler" | "client" | "exporte" | "zahlen">; weiter: (zu: string) => void }) {
  const { daten, client, exporte, zahlen } = lage;
  const b = V ? `/v/${encodeURIComponent(V)}` : null;
  const gruppen = daten?.gruppen ?? [];
  const lauf = daten?.lauf ?? null;
  // E23: „Kästchen messen“ ist CPU-Arbeit, gehört aber hinter das Lesen (Karte im Lauf), nicht in
  // die Aufbereitung nach dem Import (deren Kopf führt in den Eingang).
  // E21h: „Ortsteil-Feld zuordnen“ ebenso (CPU, je gelesener Seite).
  // E34: „Sichere Fragen übernehmen“ folgt dem Lese-Lauf (nach dem KI-Schritt) — ebenso hierher.
  const nachLesen = gruppen.filter((g) => g.schritt === "kaestchen" || g.schritt === "ortsteilfeld" || g.schritt === "fragen_sicher");
  // E21: „Ortsverzeichnis laden“ ist CPU-Arbeit, gehört aber zum Ortsverzeichnis, nicht zum Eingang.
  const orte = gruppen.filter((g) => g.schritt === "ortsimport");
  // E35: die drei Schritte der Bogenvorlage (leerer Bogen, Lesen, Anwenden) — eigener Abschnitt, Ziel Prüfen → Bogenvorlage.
  const formular = gruppen.filter((g) => FORMULAR_SCHRITTE.has(g.schritt));
  const cpu = gruppen.filter((g) => g.art === "cpu" && g.schritt !== "kaestchen" && g.schritt !== "ortsimport" && g.schritt !== "ortsteilfeld" && g.schritt !== "fragen_sicher" && !FORMULAR_SCHRITTE.has(g.schritt));
  // Die GPU-Welle des gerade laufenden Schritts zeigt der Laufblock — nicht doppelt.
  const gpu = gruppen.filter((g) => g.art === "gpu" && !(lauf && g.schritt === lauf.schritt) && !FORMULAR_SCHRITTE.has(g.schritt));
  const hoch = client.hochladen?.posten ?? [];
  const imp = client.import;
  const aktiv = zahlen.aktiv;
  const zeigeLauf = !!lauf || !!daten?.stoppt || !!daten?.gpu_fremd || (aktiv && gpu.length > 0);
  const zuletzt = zuletztFertig(daten);
  const fehlerListe = daten?.fehler ?? [];
  const link = (pfad: string, text: string) => (b ? { zu: `${b}/${pfad}`, text } : null);

  return (
    <div className="af-panel" role="none">
      <div className="af-titel" role="none">
        <span>{TX.auftraege.titel}</span>
        {V && (
          <span className="codemini" translate="no">
            {V}
          </span>
        )}
      </div>

      {hoch.length > 0 && (
        <Abschnitt titel={TX.auftraege.hochladen} icon="upload-simple" link={link("eingang", TX.auftraege.zumEingang)} weiter={weiter}>
          {hoch.map((p) => (
            <div key={p.id} className={`af-posten ${p.zustand}`}>
              <div className="af-posten-kopf">
                <span className="mono af-name" title={p.name}>
                  {p.name}
                </span>
                <span className={`af-status ${p.zustand}`}>
                  {p.zustand === "fertig" ? TX.auftraege.hochladenFertig : p.zustand === "fehler" ? TX.auftraege.fehlerChip(1) : p.zustand === "laedt" ? TX.eingang.laedt : TX.eingang.wartet}
                </span>
              </div>
              {offenPosten(p) && (
                <Fortschritt wert={mb(p.geladen)} gesamt={Math.max(0.1, mb(p.groesse))} einheit="MB" taetigkeit="hochgeladen" label={`${p.name} ${TX.eingang.laedt}`} />
              )}
            </div>
          ))}
        </Abschnitt>
      )}

      {imp && (
        <Abschnitt titel={TX.auftraege.import} icon="tray-arrow-up" link={link("eingang", TX.auftraege.zumEingang)} weiter={weiter}>
          <div className="af-posten">
            <p className="af-text">{TX.auftraege.importLaeuft(imp.dateien)}</p>
            <Fortschritt label={TX.auftraege.import} />
          </div>
        </Abschnitt>
      )}

      {aktiv && cpu.length > 0 && (
        <Abschnitt titel={TX.auftraege.aufbereitung} icon="scan" link={link("eingang", TX.auftraege.zumEingang)} weiter={weiter}>
          {cpu.map((g) => (
            <GruppeZeile key={g.schritt} g={g} />
          ))}
        </Abschnitt>
      )}

      {aktiv && orte.some((g) => offen(g) > 0) && (
        <Abschnitt titel={TX.auftraege.ortsverzeichnis} icon="map-trifold" link={link("orte?reiter=laden", TX.auftraege.zumOrtsverzeichnis)} weiter={weiter}>
          {orte.map((g) => (
            <GruppeZeile key={g.schritt} g={g} />
          ))}
        </Abschnitt>
      )}

      {aktiv && formular.some((g) => offen(g) > 0 || g.fehler > 0) && (
        <Abschnitt titel={TX.auftraege.bogenvorlage} icon="clipboard-text" link={link("pruefen/bogenvorlage", TX.auftraege.zurBogenvorlage)} weiter={weiter}>
          {formular.map((g) => (
            <GruppeZeile key={g.schritt} g={g} />
          ))}
        </Abschnitt>
      )}

      {aktiv && nachLesen.length > 0 && (
        <Abschnitt titel={TX.auftraege.nachDemLesen} icon="check-square-offset" link={link("lauf", TX.auftraege.zumLauf)} weiter={weiter}>
          {nachLesen.map((g) => (
            <GruppeZeile key={g.schritt} g={g} />
          ))}
        </Abschnitt>
      )}

      {zeigeLauf && (
        <Abschnitt titel={TX.auftraege.leseLauf} icon="cpu" link={link("lauf", TX.auftraege.zumLauf)} weiter={weiter}>
          {lauf && (
            <div className="af-posten">
              <div className="af-posten-kopf">
                <b>{auftragLabel(lauf.schritt).text}</b>
                {lauf.modell && (
                  <span className="mono af-status" translate="no">
                    {lauf.modell}
                  </span>
                )}
              </div>
              {/* Einheit und Tätigkeit des laufenden Schritts („Antwort 12 von 40 zugeordnet“); die
                  Zahlen zählt der Server über alle lauf-Zeilen desselben Kettenschritts (05 §12). */}
              <Fortschritt
                wert={lauf.seiten_fertig}
                gesamt={Math.max(1, lauf.seiten_gesamt)}
                einheit={auftragLabel(lauf.schritt).einheit}
                taetigkeit={auftragLabel(lauf.schritt).taetigkeit}
                label={auftragLabel(lauf.schritt).text}
                angehalten={!!lauf.pausiert_bis}
              />
              <div className="af-chips">
                {lauf.seiten_fehler > 0 && <Chip tone="error">{TX.auftraege.fehlerChip(lauf.seiten_fehler)}</Chip>}
                {lauf.sek_je_seite !== null && <Chip tone="neutral">{TX.lauf.jeSeite(lauf.sek_je_seite)}</Chip>}
                <Rest text={lauf.pausiert_bis ? null : restzeit(lauf.sek_je_seite, lauf.seiten_gesamt - lauf.seiten_fertig - lauf.seiten_fehler)} />
              </div>
              {lauf.pausiert_bis && (
                <p className="af-hinweis warn" role="status">
                  <Icon name="clock" size={17} />
                  <span>{pausenSatz(lauf.pausengrund, uhrzeit(lauf.pausiert_bis))}</span>
                </p>
              )}
            </div>
          )}
          {daten?.stoppt && (
            <p className="af-hinweis" role="status">
              <Icon name="pause" size={17} />
              <span>{TX.auftraege.stoppt}</span>
            </p>
          )}
          {daten?.gpu_fremd && (
            <p className="af-hinweis warn" role="status">
              <Icon name="hourglass" size={17} />
              <span>{TX.auftraege.gpuFremd}</span>
            </p>
          )}
          {aktiv && gpu.map((g) => <GruppeZeile key={g.schritt} g={g} />)}
        </Abschnitt>
      )}

      {exporte > 0 && (
        <Abschnitt titel={TX.auftraege.exporte} icon="export" link={link("exporte", TX.auftraege.zuExporten)} weiter={weiter}>
          <div className="af-posten">
            <p className="af-text">{TX.auftraege.exportLaeuft(exporte)}</p>
            <Fortschritt label={TX.auftraege.exporte} />
          </div>
        </Abschnitt>
      )}

      {!aktiv && (
        <div className="af-leer" role="none">
          <p>{TX.auftraege.leer}</p>
          {zuletzt?.zuletzt_fertig && <p className="af-zuletzt">{TX.auftraege.zuletzt(auftragLabel(zuletzt.schritt).text, zuletzt.fertig, uhrzeit(zuletzt.zuletzt_fertig))}</p>}
        </div>
      )}

      {fehlerListe.length > 0 && (
        <Abschnitt titel={TX.auftraege.fehler} icon="warning-circle" ton="fehler" weiter={weiter}>
          {fehlerListe.map((f) => {
            // E23: `kaestchen` ist CPU-Arbeit, seine Karte („Kästchenmessung“) steht aber im Lauf.
            const kaestchen = f.schritt === "kaestchen";
            // E24: die Wirkung von „Dubletten prüfen“ (Hinweise „Doppelt gescannt?“) liegt in Prüfen.
            const dublette = f.schritt === "dublette";
            const ortsimport = f.schritt === "ortsimport";
            const ortsteilfeld = f.schritt === "ortsteilfeld"; // E21h
            const fragenSicher = f.schritt === "fragen_sicher"; // E34: die Wirkung liegt in der Fragenliste
            // E33: „Vorlagen lernen“ wirkt auf die Kästchenmessung — ihre Karte steht im Lauf.
            const rahmen = f.schritt === "rahmen";
            const bogenvorlage = FORMULAR_SCHRITTE.has(f.schritt); // E35
            const istGpu = !bogenvorlage && !["raster", "maske", "boegen", "boegen_sicher", "kaestchen", "dublette", "rahmen", "ortsimport", "ortsteilfeld", "fragen_sicher"].includes(f.schritt);
            const zumLauf = istGpu || kaestchen || ortsteilfeld || rahmen;
            const ziel = dublette
              ? link("pruefen", TX.auftraege.zumPruefen)
              : fragenSicher
              ? link("pruefen/fragen", TX.auftraege.zurFragenliste)
              : bogenvorlage
              ? link("pruefen/bogenvorlage", TX.auftraege.zurBogenvorlage)
              : ortsimport
              ? link("orte?reiter=laden", TX.auftraege.zumOrtsverzeichnis)
              : link(zumLauf ? "lauf" : "eingang", zumLauf ? TX.auftraege.zumLauf : TX.auftraege.zumEingang);
            return (
              <div key={f.schritt} className="af-posten af-fehler">
                <div className="af-posten-kopf">
                  <b>{auftragLabel(f.schritt).text}</b>
                  <span className="af-status fehler">{TX.auftraege.fehlerZeile(f.anzahl, uhrzeit(f.zuletzt))}</span>
                </div>
                <p className="af-text dim">
                  {bogenvorlage ? TX.auftraege.fehlerHinweisBogenvorlage : kaestchen ? TX.auftraege.fehlerHinweisKaestchen : dublette ? TX.auftraege.fehlerHinweisDublette : fragenSicher ? TX.auftraege.fehlerHinweisFragen : ortsteilfeld ? TX.auftraege.fehlerHinweisOrtsteilfeld : ortsimport ? TX.auftraege.fehlerHinweisOrte : istGpu ? TX.auftraege.fehlerHinweisGpu : TX.auftraege.fehlerHinweisCpu}
                </p>
                {ziel && <Weiter ziel={ziel} weiter={weiter} />}
              </div>
            );
          })}
        </Abschnitt>
      )}

      {lage.ladeFehler && !daten && (
        <p className="af-hinweis" role="status">
          <Icon name="warning" size={17} />
          <span>{TX.auftraege.laedtNicht}</span>
        </p>
      )}
    </div>
  );
}

function Abschnitt({
  titel,
  icon,
  link,
  ton,
  weiter,
  children,
}: {
  titel: string;
  icon: string;
  link?: { zu: string; text: string } | null;
  ton?: "fehler";
  weiter: (zu: string) => void;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <section className={`af-abschnitt${ton ? ` ${ton}` : ""}`} role="group" aria-labelledby={id}>
      <div className="af-abschnitt-kopf">
        <Icon name={icon} size={17} />
        <h3 id={id}>{titel}</h3>
        {link && <Weiter ziel={link} weiter={weiter} />}
      </div>
      {children}
    </section>
  );
}

function Weiter({ ziel, weiter }: { ziel: { zu: string; text: string }; weiter: (zu: string) => void }) {
  return (
    <button type="button" role="menuitem" className="af-weiter ring-focus" onClick={() => weiter(ziel.zu)}>
      {ziel.text}
      <Icon name="arrow-right" size={15} />
    </button>
  );
}

function Rest({ text }: { text: string | null }) {
  return text ? <span className="af-rest">{text}</span> : null;
}

function GruppeZeile({ g }: { g: AuftragsGruppe }) {
  const l = auftragLabel(g.schritt);
  const o = offen(g);
  // Ein einzelner langer Auftrag (z. B. „Dubletten prüfen“: einer je Verfahren) hat keinen
  // zählbaren Fortschritt — „0 von 1“ stünde minutenlang still. Dann: unbestimmtes Band + seit wann.
  const einzeln = g.gesamt === 1 && g.laeuft === 1;
  return (
    <div className="af-posten" data-schritt={g.schritt}>
      <div className="af-posten-kopf">
        <b>{l.text}</b>
      </div>
      {einzeln ? (
        <Fortschritt label={l.text} />
      ) : (
        <Fortschritt wert={g.fertig} gesamt={Math.max(1, g.gesamt)} einheit={l.einheit} taetigkeit={l.taetigkeit} label={l.text} />
      )}
      <div className="af-chips">
        {g.laeuft > 0 && <Chip tone="teal">{einzeln && g.seit ? TX.auftraege.laeuftSeit(uhrzeit(g.seit)) : TX.auftraege.laeuft(g.laeuft)}</Chip>}
        {g.wartet > 0 && <Chip tone="neutral">{TX.auftraege.wartet(g.wartet)}</Chip>}
        {g.fehler > 0 && <Chip tone="error">{TX.auftraege.fehlerChip(g.fehler)}</Chip>}
        {!o && g.zuletzt_fertig && (
          <Chip tone="good" icon="check">
            {TX.auftraege.fertigUm(uhrzeit(g.zuletzt_fertig))}
          </Chip>
        )}
        <Rest text={o ? restzeit(g.sek_je_posten, o) : null} />
      </div>
    </div>
  );
}

/* ================================================================ Linie == */

/** Die dünne Linie am unteren Rand der Kopfleiste, solange etwas läuft: Gesamtfortschritt der
 *  offenen Wellen (+ Lauf), ohne zählbare Einheiten ein wanderndes Band. Rein schmückend — die
 *  Zahlen stehen im Panel und im Namen des Knopfs. */
export function AuftraegeLinie({ V }: { V: string | null }) {
  const { zahlen: z } = useAuftraegeLage(V, false);
  if (!z.aktiv) return null;
  const bestimmt = z.gesamt > 0;
  const anteil = bestimmt ? Math.max(0.02, Math.min(1, z.erledigt / z.gesamt)) : 0;
  return (
    <div className="af-linie" aria-hidden="true" data-art={bestimmt ? "bestimmt" : "unbestimmt"} data-testid="auftraege-linie">
      <span className="af-linie-fuellung" style={bestimmt ? { transform: `scaleX(${anteil.toFixed(3)})` } : undefined} />
    </div>
  );
}
